import { AxiosError, AxiosResponse } from 'axios'
import { Context } from '../server-middleware/context'
import { z } from 'zod'

const isUserTitleEnabled = (customoptions: string): boolean => {
  return Boolean(parseInt(customoptions) & 2)
}

const getUserTitle = (customoptions: string, usertitle: string, rankName: string) => {
  if (!isUserTitleEnabled(customoptions)) return ''

  if (usertitle) return usertitle

  return rankName
}

const userSchema = z.object({
  avatar: z.string(),
  userid: z.string().transform(id => parseInt(id)),
  username: z.string(),
  usertitle: z.string(),
  rank_id: z.string().nullable(),
  rank_display_name: z.string().nullable(),
  rank_avatar_icon_thumb: z.string().nullable(),
  cdn: z.string(),
  customoptions: z.string()
}).transform(user => {
  const { avatar, userid, username, usertitle, customoptions } = user

  return {
    avatar,
    userid,
    username,
    usertitle: getUserTitle(customoptions, usertitle, user.rank_display_name || ''),
    rank: user.rank_id === null
      ? null
      : {
          name: `${user.rank_display_name}`,
          avatarIconUrl: user.rank_avatar_icon_thumb ? `${user.cdn}${user.rank_avatar_icon_thumb}` : ''
        }
  }
})

export type User = z.infer<typeof userSchema>

const fallback: (userId: number) => User = (userId) => ({
  avatar: '',
  userid: userId,
  username: 'Anonymous',
  usertitle: '',
  rank: null
})

export interface BadgeAssignment {
  addeddate: string
  _related: {
    badges: {
      title: string
      imageAttachmentId: string
    }
    user: {
      userid: string
      username: string
    }
  }
}

class UserAPI {
  url: string
  context: Context
  cache: {[userId: number]: User | undefined}

  constructor (context: Context) {
    this.context = context
    this.cache = {}
    const host = process.env.app_config_api_host
    this.url = host ? `https://${host}` : 'http://api.dev.insided.com'
  }

  public async getUser (userId: number): Promise<User> {
    if (!userId) {
      return fallback(0)
    }

    const cached = this.cache[userId]
    if (cached) {
      return cached
    }

    try {
      const response: AxiosResponse<User> = await this.context.axios.get(`${this.url}/user/${userId}`, {
        headers: {
          'X-Consumer-Custom-Id': this.context.yipId
        }
      })
      const parseResult = userSchema.safeParse(response.data)
      if (!parseResult.success) {
        this.context.logger(parseResult.error, 'warning')
      }
      this.cache[userId] = parseResult.success ? parseResult.data : response.data
      return response.data
    } catch (e) {
      const err: AxiosError = e
      this.context.logger(err)

      return fallback(userId)
    }
  }

  public async getUsers (userIds: number[]): Promise<Record<string, User|undefined>> {
    const uncachedIds = userIds.filter(id => !this.cache[id])

    if (!uncachedIds) return this.cache

    try {
      const q = uncachedIds.map(uid => `filter%5Bt.userid%5D%5B%5D=${uid}`).join('&')
      const res: AxiosResponse<Record<string, User>> = await this.context.axios.get(`${this.url}/crud/viewuser?${q}`, {
        headers: {
          'X-Consumer-Custom-Id': this.context.yipId
        }
      })
      Object.keys(res.data).forEach(key => {
        const u = res.data[key]

        const parseResult = userSchema.safeParse(u)
        if (!parseResult.success) {
          this.context.logger(parseResult.error, 'warning')
        }

        this.cache[u.userid] = parseResult.success ? parseResult.data : u
      })
      return this.cache
    } catch (e) {
      const err: AxiosError = e
      this.context.logger(err)

      return this.cache
    }
  }

  public async getLatestBadgeAssignments (amount: number): Promise<BadgeAssignment[]> {
    const res = await this.context.axios.get<BadgeAssignment[]>(`${this.url}/user/badges?limit=${amount}`, {
      headers: {
        'X-Consumer-Custom-Id': this.context.yipId
      }
    })

    return res.data
  }
}

export default UserAPI
